import axios from 'axios';
import store from '../configure-store';
import { ShowSessionExpiredAction } from 'actions';

// Declare constants from environment variables
declare const AUTH0_DOMAIN: string;
declare const AUTH0_CLIENT_ID: string;
declare const BASE_URL: string;
declare const EXTERNAL_URL: string;

// Utility to get the window location in a safe way
const getWindowLocation = () => {
  if (typeof window !== 'undefined' && window.location) {
    return window.location;
  }
  return { href: '', origin: '' };
};

// Utility to get localStorage in a safe way
const getLocalStorage = () => {
  if (typeof window !== 'undefined' && window.localStorage) {
    return window.localStorage;
  }
  return null;
};

// Function to log out the user and clear the session
export const logoutAndClearSession = () => {
  const { origin } = getWindowLocation();
  const returnTo = encodeURIComponent(origin);
  const logoutUrl = `https://${AUTH0_DOMAIN}/v2/logout?client_id=${AUTH0_CLIENT_ID}&returnTo=${returnTo}`;
  window.location.href = logoutUrl;
};

export interface IIdSkPayload {
  id: string;
  sk: string;
}

// Create a generic HTTP service instance
const createHttpService = (baseURL: string) => {
  const instance = axios.create({ baseURL });

  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        store.dispatch(ShowSessionExpiredAction());
      }
      return Promise.reject(error);
    },
  );

  return instance;
};

// Export HTTP service instances
export const getHttpService = () => createHttpService(BASE_URL);
export const getHttpServiceAdmin = () => createHttpService(`${BASE_URL}/admin`);
export const getHttpServiceExternal = () => {
  const instance = createHttpService(EXTERNAL_URL);

  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        logoutAndClearSession();
      }
      return Promise.reject(error);
    },
  );

  return instance;
};

// Define the type for header options
export interface IHeaderOption extends Record<string, string> {
  'content-type': string;
  Authorization: string;
}

// Global ID token cache (API expects ID token, not access token)
let globalIdToken: string | null = null;

export const setGlobalIdToken = (token: string) => {
  globalIdToken = token;
};

// Function to get header options with the authorization token
export const getHeaderOptions = (): IHeaderOption => {
  if (!globalIdToken) {
    throw new Error('ID token not available. Please ensure user is authenticated.');
  }
  
  return {
    'content-type': 'application/json',
    Authorization: `Bearer ${globalIdToken}`,
  };
};

